import _objectSpread from "@babel/runtime/helpers/objectSpread";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _excluded = ["type"];
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { useEffect, useRef } from 'react';
import { matchPath } from 'react-router';
import { useHistory, useLocation } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import { availableRoutes } from '@magento/venia-ui/lib/components/Routes/routes';
import { useAppContext } from '../context/app';
import { useRootComponents } from '../context/rootComponents';
import mergeOperations from '../util/shallowMerge';
import { getComponentData } from '../util/magentoRouteData';
import DEFAULT_OPERATIONS from '../talons/MagentoRoute/magentoRoute.gql';
import { getRootComponent } from '../talons/MagentoRoute/helpers';
var DELAY_MESSAGE_PREFIX = 'DELAY:';

var useDelayedTransition = function useDelayedTransition() {
  var _useLocation = useLocation(),
      pathname = _useLocation.pathname;

  var history = useHistory();
  var client = useApolloClient();
  var operations = mergeOperations(DEFAULT_OPERATIONS);
  var resolveUrlQuery = operations.resolveUrlQuery;

  var _useRootComponents = useRootComponents(),
      _useRootComponents2 = _slicedToArray(_useRootComponents, 2),
      setComponentMap = _useRootComponents2[1];

  var _useAppContext = useAppContext(),
      _useAppContext2 = _slicedToArray(_useAppContext, 2),
      appApi = _useAppContext2[1];

  var appActions = appApi.actions;
  var setPageLoading = appActions.setPageLoading;
  var unblock = useRef();
  useEffect(function () {
    // Override globalThis.addEventListener to prevent binding beforeunload while we add our blocker
    var originalWindowAddEventListener = globalThis.addEventListener;

    globalThis.addEventListener = function (type, listener, options) {
      if (type === 'beforeunload') {
        return;
      }

      if (typeof originalWindowAddEventListener === 'function') {
        return originalWindowAddEventListener(type, listener, options);
      }
    };

    unblock.current = history.block(function (location) {
      var currentPath = pathname;

      if (process.env.USE_STORE_CODE_IN_URL === 'true') {
        var storeCodes = AVAILABLE_STORE_VIEWS.map(function (store) {
          return "/?".concat(store.store_code);
        }).join('|');
        var regex = new RegExp("^".concat(storeCodes));
        currentPath = currentPath.replace(regex, '');
      } // Ignore query string changes


      if (location.pathname === currentPath) {
        return true;
      } // Ignore hardcoded routes


      var isInternalRoute = availableRoutes.some(function (_ref) {
        var path = _ref.pattern,
            exact = _ref.exact;
        return !!matchPath(location.pathname, {
          path: path,
          exact: exact
        });
      });

      if (isInternalRoute) {
        return true;
      }

      return "".concat(DELAY_MESSAGE_PREFIX).concat(location.pathname);
    });
    globalThis.addEventListener = originalWindowAddEventListener;
    return function () {
      if (typeof unblock.current === 'function') {
        unblock.current();
      }
    };
  }, [pathname, history]);
  useEffect(function () {
    globalThis.handleRouteChangeConfirmation = /*#__PURE__*/function () {
      var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(message, proceed) {
        var currentPathname, queryResult, data, _ref3, route, _ref4, type, routeData, rootComponent;

        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                if (!globalThis.avoidDelayedTransition) {
                  _context.next = 4;
                  break;
                }

                globalThis.avoidDelayedTransition = false;

                if (typeof unblock.current === 'function') {
                  unblock.current();
                }

                return _context.abrupt("return", proceed(true));

              case 4:
                setPageLoading(true);
                currentPathname = message.replace(DELAY_MESSAGE_PREFIX, '');
                _context.next = 8;
                return client.query({
                  query: resolveUrlQuery,
                  fetchPolicy: 'cache-first',
                  nextFetchPolicy: 'cache-first',
                  variables: {
                    url: currentPathname
                  }
                });

              case 8:
                queryResult = _context.sent;
                data = queryResult.data;
                _ref3 = data || {}, route = _ref3.route;
                _ref4 = route || {}, type = _ref4.type, routeData = _objectWithoutProperties(_ref4, _excluded);

                if (!type) {
                  _context.next = 17;
                  break;
                }

                _context.next = 15;
                return getRootComponent(type);

              case 15:
                rootComponent = _context.sent;
                setComponentMap(function (prevMap) {
                  return new Map(prevMap).set(currentPathname, _objectSpread({
                    component: rootComponent
                  }, getComponentData(routeData), {
                    type: type
                  }));
                });

              case 17:
                setPageLoading(false);

                if (typeof unblock.current === 'function') {
                  unblock.current();
                }

                proceed(true);

              case 20:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }));

      return function (_x, _x2) {
        return _ref2.apply(this, arguments);
      };
    }();
  }, [client, resolveUrlQuery, setComponentMap, setPageLoading]);
};

export default useDelayedTransition;